import DOMPurify from "dompurify";

// HTML that comes from outside the platform (email bodies, uploaded and converted
// files) is shown in an iframe sandboxed with no permissions, and sanitized as well so
// that nothing active is left in it even if the frame is ever opened on its own.
//
// The whole document is kept, not just its body: converted spreadsheets carry all
// their formatting in a <style> in the head. LibreOffice writes cell notes as
// <comment> elements that its own stylesheet hides, so they are kept too. The doctype
// is kept as well: without it the frame renders in quirks mode, where tables stop
// inheriting fonts from the page.
export const sanitizeHtml = (html) =>
  DOMPurify.sanitize(html ?? "", {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ["!doctype", "comment"],
  });

const readBlobAsText = (blob, encoding) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob, encoding);
  });

const DECLARED_CHARSET = /<meta[^>]+charset\s*=\s*["']?\s*([\w.:-]+)/i;

// Reads an HTML file as the browser would have when it loaded the file itself: a
// charset declared near the top of the document wins, otherwise UTF-8. Word's "Save as
// Web Page", for one, writes windows-1252.
export const readHtmlBlob = async (blob) => {
  const text = await readBlobAsText(blob);
  const charset = DECLARED_CHARSET.exec(text.slice(0, 1024))?.[1];
  if (!charset || /^utf-?8$/i.test(charset)) {
    return text;
  }
  try {
    return await readBlobAsText(blob, charset);
  } catch {
    return text;
  }
};
